import { useState, useEffect } from "react";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Checkbox from "../../components/common/Checkbox";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import Badge from "../../components/common/Badge";
import { useStore } from "../../context/StoreContext";
import {
  useGetOrderTypesQuery,
  useUpdateOrderTypesMutation,
} from "../../store/api";
import {
  createOrderTypesForm,
  toOrderTypesPayload,
} from "./orderTypesMapper";

const OrderTypes = () => {
  const { selectedStoreId } = useStore();
  const {
    data: orderTypes,
    isLoading,
    isError,
    error,
    refetch: refetchOrderTypes,
  } = useGetOrderTypesQuery(selectedStoreId, {
    skip: !selectedStoreId,
  });
  const [updateOrderTypes, updateState] = useUpdateOrderTypesMutation();

  const [form, setForm] = useState({
    ...createOrderTypesForm(),
    curbside: false,
  });

  useEffect(() => {
    if (orderTypes) {
      setForm((prev) => ({
        ...prev,
        ...createOrderTypesForm(orderTypes),
        curbside: Boolean(orderTypes?.curbside),
      }));
    }
  }, [orderTypes]);

  const handleChange = (e) => {
    const { name, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStoreId) {
      alert("Please select a store first");
      return;
    }
    try {
      const response = await updateOrderTypes({
        storeId: selectedStoreId,
        payload: toOrderTypesPayload(form),
      }).unwrap();
      if (response && typeof response === "object") {
        setForm(createOrderTypesForm(response));
      } else {
        await refetchOrderTypes();
      }
    } catch (err) {
      // error handled by UI
    }
  };

  const orderTypeInfo = [
    {
      key: "dineIn",
      label: "Dine-In",
      description: "Customers order from their table using QR codes",
      icon: (
        <svg
          className="h-6 w-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
          />
        </svg>
      ),
    },
    {
      key: "takeout",
      label: "Takeout / Pickup",
      description: "Customers order online and pick up at the restaurant",
      icon: (
        <svg
          className="h-6 w-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
          />
        </svg>
      ),
    },
    {
      key: "delivery",
      label: "Delivery",
      description: "Orders are delivered to customer's address",
      icon: (
        <svg
          className="h-6 w-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0"
          />
        </svg>
      ),
    },
    {
      key: "curbside",
      label: "Curbside Pickup",
      description: "Customers order online and pick up from their car",
      icon: (
        <svg
          className="h-6 w-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
          />
        </svg>
      ),
    },
  ];

  if (!selectedStoreId) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Order Types"
          subtitle="Configure available order types for your store."
        />
        <Card>
          <Alert tone="warning">
            Please select a store to configure order types.
          </Alert>
        </Card>
      </div>
    );
  }

  const enabledCount = Object.values(form).filter(Boolean).length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Order Types"
        subtitle="Enable or disable different ways customers can place orders."
      />

      <Card>
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              Available Order Types
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              Select which order types are available for this store
            </p>
          </div>
          <Badge tone={enabledCount > 0 ? "success" : "neutral"}>
            {enabledCount} Enabled
          </Badge>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="grid gap-4 md:grid-cols-2">
            {orderTypeInfo.map((orderType) => (
              <div
                key={orderType.key}
                className={`rounded-lg border-2 p-4 transition ${
                  form[orderType.key]
                    ? "border-[#4f46e5] bg-indigo-50"
                    : "border-slate-200 bg-white"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`rounded-lg p-2 ${
                      form[orderType.key]
                        ? "bg-[#4f46e5] text-white"
                        : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {orderType.icon}
                  </div>
                  <div className="flex-1">
                    <Checkbox
                      label={orderType.label}
                      name={orderType.key}
                      checked={form[orderType.key]}
                      onChange={handleChange}
                    />
                    <p className="mt-1 text-xs text-slate-600">
                      {orderType.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {isLoading && (
            <Alert tone="info">Loading current configuration...</Alert>
          )}
          {isError && (
            <Alert tone="error">
              {error?.data?.detail || "Failed to load order types."}
            </Alert>
          )}
          {updateState.isSuccess && (
            <Alert tone="success">
              Order types updated successfully. Changes are now live.
            </Alert>
          )}
          {updateState.isError && (
            <Alert tone="error">
              {updateState.error?.data?.detail ||
                "Failed to update order types."}
            </Alert>
          )}

          <div className="flex gap-3">
            <Button type="submit" disabled={updateState.isLoading}>
              {updateState.isLoading ? "Saving..." : "Save Order Types"}
            </Button>
          </div>
        </form>
      </Card>

      <Card className="bg-amber-50">
        <div className="flex gap-3">
          <svg
            className="h-5 w-5 flex-shrink-0 text-amber-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <div>
            <h4 className="text-sm font-semibold text-amber-900">
              Important Notes
            </h4>
            <ul className="mt-2 space-y-1 text-sm text-amber-800">
              <li>
                • Disabling an order type will hide it from customers
                immediately
              </li>
              <li>
                • For Dine-In orders, make sure you have configured your
                floorplan and tables
              </li>
              <li>
                • For Delivery, ensure you have delivery zones and fees
                configured
              </li>
              <li>
                • At least one order type should be enabled for customers to
                place orders
              </li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default OrderTypes;
